import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { t as translate } from 'i18next'
import { alpha } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import Button from '@mui/material/Button'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'

import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

import DialogFormTariff from './dialog-tariff'
import { useDeleteTariffMutation } from './tariff-api'
import TableRowsSkeleton from '@/components/TableRowsSkeleton'
import Stack from '@mui/material/Stack'

// ═══════════════════════════════════════════════════════════════════════════
// КОНСТАНТЫ ДЛЯ ТИПОВ ТАРИФОВ
// ═══════════════════════════════════════════════════════════════════════════

const TARIFF_TYPE_CONFIG = {
  simple: {
    labelKey: 'tariff_type_simple',
    color: 'success',
    icon: AccessTimeIcon
  },
  hourly: {
    labelKey: 'tariff_type_simple',
    color: 'success',
    icon: AccessTimeIcon
  },
  threshold: {
    labelKey: 'tariff_type_threshold',
    color: 'warning',
    icon: TrendingUpIcon
  },
  daily: {
    labelKey: 'tariff_type_daily_cap',
    color: 'info',
    icon: AccessTimeIcon
  },
  daily_cap: {
    labelKey: 'tariff_type_daily_cap',
    color: 'info',
    icon: AccessTimeIcon
  },
  progressive: {
    labelKey: 'tariff_type_progressive',
    color: 'secondary',
    icon: TrendingUpIcon
  },
  vehicle_type: {
    labelKey: 'tariff_type_vehicle',
    color: 'error',
    icon: DirectionsCarIcon
  },
  time_based: {
    labelKey: 'tariff_type_time_based',
    color: 'secondary',
    icon: WbSunnyIcon
  },
  mrp: {
    labelKey: 'tariff_type_mrp',
    color: 'default',
    icon: AccessTimeIcon
  },
  complex: {
    labelKey: 'tariff_type_complex',
    color: 'default',
    icon: AccessTimeIcon
  }
}

/**
 * Получить конфиг типа тарифа
 */
const getTariffTypeConfig = (type) => {
  return (
    TARIFF_TYPE_CONFIG[type] || {
      labelKey: null,
      fallbackLabel: type || translate('tariff_not_specified'),
      color: 'default',
      icon: AccessTimeIcon
    }
  )
}

/**
 * Форматирование суммы
 */
const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '—'
  return translate('format_tenge', { value: amount.toLocaleString() })
}

/**
 * Форматирование минут
 */
const formatMinutes = (minutes) => {
  if (!minutes && minutes !== 0) return translate('duration_zero_minutes')
  if (minutes < 60) {
    return translate('duration_minutes_short', { value: minutes })
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return translate('duration_hours_short', { value: hours })
  return translate('duration_hours_minutes_short', { hours, minutes: mins })
}

const swallowError = (error) => Boolean(error)

const DialogTable = ({ tariffs = [], buildingId, isLoading = false }) => {
  const { t } = useTranslation()
  const [deleteTariff] = useDeleteTariffMutation()
  const [currentId, setCurrentId] = useState()
  const [openTariff, setOpenTariff] = useState(false)
  const [approve, setApprove] = useState(false)

  const handleEdit = (id) => () => {
    setOpenTariff(true)
    setCurrentId(id)
  }

  const handleDelete = async () => {
    try {
      await deleteTariff(currentId).unwrap()
      setApprove(false)
    } catch (err) {
      swallowError(err)
    }
  }

  const handleModalDelete = (id) => () => {
    setCurrentId(id)
    setApprove(true)
  }

  const cancelDelete = () => {
    setApprove(false)
    setCurrentId(null)
  }

  const handleCloseTariff = () => {
    setOpenTariff(false)
    setCurrentId(null)
  }

  return (
    (isLoading || tariffs) && (
      <>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.02)
                : theme.palette.background.paper
          }}
        >
          <Table aria-label="tariffs table" size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.04)
                      : theme.palette.grey[100]
                }}
              >
                <TableCell>{t('payment_partners_name')}</TableCell>
                <TableCell>{t('type')}</TableCell>
                <TableCell align="right">{t('sum')}</TableCell>
                <TableCell align="right">
                  {t('tariff_free_minutes_short')}
                </TableCell>
                <TableCell align="right">{t('status')}</TableCell>
                <TableCell align="right">{t('code')}</TableCell>
                <TableCell align="right">{t('options')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                isLoading ? (
                  <TableRowsSkeleton rows={5} columns={7} />
                ) : (
                  tariffs?.map((tariff) => {
                    const typeConfig = getTariffTypeConfig(
                      tariff.tariff_type || tariff.calc_mode
                    )
                    const TypeIcon = typeConfig.icon

                    return (
                      <TableRow
                        key={tariff.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {/* Название */}
                        <TableCell component="th" scope="row">
                          <Box sx={{ fontWeight: 500 }}>{tariff.name}</Box>
                        </TableCell>

                        {/* Тип тарифа */}
                        <TableCell>
                          <Chip
                            icon={<TypeIcon sx={{ fontSize: 16 }} />}
                            label={
                              typeConfig.labelKey
                                ? t(typeConfig.labelKey)
                                : typeConfig.fallbackLabel
                            }
                            color={typeConfig.color}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </TableCell>

                        {/* Сумма */}
                        <TableCell align="right">
                          <Box sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatAmount(tariff.amount)}
                          </Box>
                          {/* Показываем доп. инфо для threshold */}
                          {tariff.threshold_minutes > 0 &&
                            tariff.daily_rate > 0 && (
                              <Box
                                sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                              >
                                {t('tariff_extra_rate_after_threshold', {
                                  rate: formatAmount(tariff.daily_rate),
                                  minutes: formatMinutes(tariff.threshold_minutes)
                                })}
                              </Box>
                            )}
                        </TableCell>

                        {/* Бесплатные минуты */}
                        <TableCell align="right">
                          {tariff.free_minutes > 0 ? (
                            <Chip
                              label={formatMinutes(tariff.free_minutes)}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          ) : (
                            <Box sx={{ color: 'text.disabled' }}>—</Box>
                          )}
                        </TableCell>

                        {/* Статус */}
                        <TableCell align="right">
                          <Chip
                            label={
                              tariff.is_active ? t('actively') : t('inactively')
                            }
                            color={tariff.is_active ? 'success' : 'default'}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                        </TableCell>

                        {/* Код */}
                        <TableCell align="right">
                          <Box
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: 'text.secondary'
                            }}
                          >
                            {tariff.code || '—'}
                          </Box>
                        </TableCell>

                        {/* Действия */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t('edit')}>
                              <IconButton
                                size="small"
                                aria-label="edit"
                                onClick={handleEdit(tariff.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('delete')}>
                              <IconButton
                                size="small"
                                aria-label="remove"
                                color="error"
                                onClick={handleModalDelete(tariff.id)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )
              }
            </TableBody>
          </Table>
        </TableContainer>

        {/* Диалог редактирования тарифа */}
        <DialogFormTariff
          open={openTariff}
          buildingId={buildingId}
          onClose={handleCloseTariff}
          currentId={currentId}
        />

        {/* Диалог подтверждения удаления */}
        <Dialog open={approve} fullWidth maxWidth="xs">
          <DialogTitle>{t('confirmation')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('do_you_really_want_to_delete_this_tariff')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete} color="inherit">
              {t('cancel')}
            </Button>
            <Button color="error" variant="contained" onClick={handleDelete}>
              {t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  )
}

export default DialogTable